import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, MapPin, Truck, Store, CheckCircle, ChevronRight, 
  AlertCircle, Plus, Wallet, X, Info, Copy, Calendar, Clock, 
  Sparkles, ShieldCheck, Check 
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import { useClientSession } from '@/lib/ClientSessionContext';
import { formatTanggalIndo } from '@/lib/timeUtils';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

export default function ClientPOCheckout({ 
  products, 
  legalitas, 
  totalHargaJualSemua, 
  botolList, 
  tutupList = [], 
  sprayList = [], 
  itemTambahanList = [], 
  activeBrand = 'Brand Anda',
  brandName,
  matchedClient,
  onBack, 
  onComplete 
}) {
  const { accountsMap } = useBankAccounts();
  const { clientUser } = useClientSession();

  const effectiveClient = matchedClient || clientUser;
  const effectiveBrand = brandName || activeBrand || effectiveClient?.brand_name || 'Brand Anda';

  // Filter allowed payment terms configured by office (Cash vs Tempo)
  const allowedTerms = useMemo(() => {
    const terms = effectiveClient?.allowed_payment_terms;
    if (terms && Array.isArray(terms) && terms.length > 0) {
      const mapped = terms.map(t => t === 'dp' ? 'cash' : t);
      const unique = Array.from(new Set(mapped)).filter(t => t === 'cash' || t === 'tempo');
      if (unique.length > 0) return unique;
    }
    return ['cash', 'tempo'];
  }, [effectiveClient]);

  const [deliveryMethod, setDeliveryMethod] = useState('kurir'); // 'kurir' | 'pickup'
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addresses, setAddresses] = useState(() => {
    if (effectiveClient?.addresses && Array.isArray(effectiveClient.addresses) && effectiveClient.addresses.length > 0) {
      return effectiveClient.addresses.map((a, i) => ({
        id: a.id || i + 1,
        label: a.label || a.nama_alamat || 'Alamat Klien',
        isUtama: i === 0,
        nama: `${effectiveBrand} / ${effectiveClient?.pic_name || effectiveClient?.owner_name || 'Client'} - ${effectiveClient?.phone || '08xxxxxxxx'}`,
        alamatLengkap: a.alamat_lengkap || a.nama_alamat || ''
      }));
    }
    return [
      { id: 1, label: 'Kantor', isUtama: true, nama: 'Satala Dermatech Essential / Rifay - 08567215745', alamatLengkap: 'Jalan aster kavling Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat 16610' }
    ];
  });
  const [selectedAddressId, setSelectedAddressId] = useState(1);

  // Termin Pembayaran: User diminta pilih salah satu ('cash' | 'tempo')
  const [paymentTerms, setPaymentTerms] = useState(() => {
    const terms = effectiveClient?.allowed_payment_terms;
    if (terms && Array.isArray(terms) && terms.length > 0) {
      const first = terms[0] === 'dp' ? 'cash' : terms[0];
      if (first === 'cash' || first === 'tempo') return first;
    }
    return 'cash';
  }); 

  React.useEffect(() => {
    if (allowedTerms.length > 0 && !allowedTerms.includes(paymentTerms)) {
      setPaymentTerms(allowedTerms[0]);
    }
  }, [allowedTerms, paymentTerms]);

  const [tempoDays, setTempoDays] = useState(() => Number(effectiveClient?.tempo_duration_days) || 30); // 14 | 30 | 45 | 60

  React.useEffect(() => {
    if (effectiveClient?.tempo_duration_days) {
      setTempoDays(Number(effectiveClient.tempo_duration_days));
    }
  }, [effectiveClient]);

  // Helper to find default bank key based on paymentTerms ('tempo' -> ASB, others -> Satala Dermatech Essential)
  const getDefaultBankKey = (terms) => {
    if (terms === 'tempo') {
      const asbKey = Object.keys(accountsMap).find(k => {
        const name = (accountsMap[k]?.name || '').toLowerCase();
        return name.includes('apa saja') || name.includes('asb');
      });
      return asbKey || 'bca';
    } else {
      const satalaKey = Object.keys(accountsMap).find(k => {
        const name = (accountsMap[k]?.name || '').toLowerCase();
        return name.includes('satala');
      });
      return satalaKey || 'bni';
    }
  };

  // Rekening Tujuan Transfer: preselect based on assigned_bank_account or paymentTerms default
  const [selectedBankKey, setSelectedBankKey] = useState(() => {
    if (effectiveClient?.assigned_bank_account && effectiveClient.assigned_bank_account !== 'all') {
      return effectiveClient.assigned_bank_account;
    }
    return getDefaultBankKey(paymentTerms);
  });

  React.useEffect(() => {
    if (effectiveClient?.assigned_bank_account && effectiveClient.assigned_bank_account !== 'all') {
      setSelectedBankKey(effectiveClient.assigned_bank_account);
    } else {
      // Default: kalau tempo otomatis kepilih yang ASB, selain tempo kepilih Satala Dermatech Essential
      setSelectedBankKey(getDefaultBankKey(paymentTerms));
    }
  }, [paymentTerms, effectiveClient, accountsMap]);

  const [selectedCourier, setSelectedCourier] = useState('JNE REG');
  const [showCourierModal, setShowCourierModal] = useState(false);
  const [showPaymentSimulationModal, setShowPaymentSimulationModal] = useState(false);
  const [copiedAccount, setCopiedAccount] = useState(false);

  // Form State untuk Tambah Alamat
  const [newAddress, setNewAddress] = useState({ label: 'Rumah', nama: '', alamatLengkap: '' });
  const selectedAddress = addresses.find(a => a.id === selectedAddressId);

  // Bank accounts list
  const bankOptions = useMemo(() => {
    const defaultAccounts = [
      {
        key: 'bni',
        defaultBank: 'BCA (Bank Central Asia)',
        defaultName: 'Satala Dermatech Essential',
        defaultNum: '6821535384',
        defaultBadge: 'Rekening Utama Satala'
      },
      {
        key: 'bca',
        defaultBank: 'BCA (Bank Central Asia)',
        defaultName: 'Apa Saja Bisa',
        defaultNum: '1741359516',
        defaultBadge: 'Rekening Operasional (ASB)'
      },
      {
        key: 'mandiri',
        defaultBank: 'BCA (Bank Central Asia)',
        defaultName: 'Cuan Jalan Terus',
        defaultNum: '1741359958',
        defaultBadge: 'Rekening Operasional'
      }
    ];

    const keys = Object.keys(accountsMap).length > 0 ? Object.keys(accountsMap) : defaultAccounts.map(d => d.key);

    const all = keys.map((key, idx) => {
      const dbAcc = accountsMap[key] || {};
      const def = defaultAccounts.find(d => d.key === key) || {};
      const rawBank = (dbAcc.nama_bank || '').toUpperCase();
      const bankTitle = rawBank === 'BCA' ? 'BCA (Bank Central Asia)' : rawBank === 'MANDIRI' ? 'Bank Mandiri' : rawBank || def.defaultBank || 'BCA (Bank Central Asia)';

      return {
        key,
        bankName: bankTitle,
        accountName: dbAcc.name || def.defaultName || 'PT. Satala Dermatech Essential',
        accountNumber: dbAcc.number || def.defaultNum || '',
        badge: dbAcc.label || def.defaultBadge || (idx === 0 ? 'Rekening Utama Satala' : 'Rekening Operasional'),
        isDefault: idx === 0
      };
    });

    if (clientUser?.assigned_bank_account && clientUser.assigned_bank_account !== 'all') {
      const assigned = all.filter(b => b.key === clientUser.assigned_bank_account);
      if (assigned.length > 0) {
        return assigned.map(b => ({ ...b, badge: 'Rekening Resmi Ditugaskan' }));
      }
    }
    return all;
  }, [accountsMap, clientUser]);

  const activeBank = bankOptions.find(b => b.key === selectedBankKey) || bankOptions[0];

  // Kalkulasi Tanggal Jatuh Tempo jika Tempo
  const jatuhTempoDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + (parseInt(tempoDays) || 30));
    return d;
  }, [tempoDays]);

  const jatuhTempoStr = useMemo(() => {
    return formatTanggalIndo(jatuhTempoDate.toISOString());
  }, [jatuhTempoDate]);

  // Kalkulasi Nominal Bayar Sekarang & Sisa Pelunasan:
  // Cash: DP 50% di awal, sisa pelunasan 50% ketika barang siap kirim.
  // Tempo: Bayar 0 sekarang, pelunasan 100% pada tanggal jatuh tempo.
  const { nominalBayarSekarang, sisaPelunasan, deskripsiTermin } = useMemo(() => {
    if (paymentTerms === 'cash') {
      const dp50 = Math.round(totalHargaJualSemua * 0.5);
      return {
        nominalBayarSekarang: dp50,
        sisaPelunasan: totalHargaJualSemua - dp50,
        deskripsiTermin: 'DP 50% di Awal'
      };
    }
    if (paymentTerms === 'tempo') {
      return {
        nominalBayarSekarang: 0,
        sisaPelunasan: totalHargaJualSemua,
        deskripsiTermin: `Tempo ${tempoDays} Hari (Jatuh Tempo: ${jatuhTempoStr})`
      };
    }
    return { 
      nominalBayarSekarang: Math.round(totalHargaJualSemua * 0.5), 
      sisaPelunasan: totalHargaJualSemua - Math.round(totalHargaJualSemua * 0.5), 
      deskripsiTermin: 'DP 50% di Awal' 
    };
  }, [paymentTerms, tempoDays, totalHargaJualSemua, jatuhTempoStr]);

  // Kalkulasi Berat & Defisit Stok
  const { orderStats, stockDeficits } = useMemo(() => {
    let totalGram = 0;
    let totalQty = 0;
    const deficits = [];
    const usageMap = {};

    products.forEach(p => {
      const qty = p.qty || 1;
      totalQty += qty;

      const botol = p.botol_id ? botolList?.find(b => b.id === p.botol_id) : null;
      const ukuranMl = p.botol_dari_client ? (p.botol_ukuran_client || 0) : (botol?.ukuran_label_ml || 0);
      const beratBotol = botol?.berat_gram || 50;

      if (botol) {
        const key = `botol_${botol.id}`;
        usageMap[key] = usageMap[key] || { needed: 0, item: botol, tipe: 'Botol' };
        usageMap[key].needed += qty;
      }

      const tutup = p.tutup_id ? tutupList?.find(t => t.id === p.tutup_id) : null;
      const beratTutup = tutup?.berat_gram || 10;
      if (tutup) {
        const key = `tutup_${tutup.id}`;
        usageMap[key] = usageMap[key] || { needed: 0, item: tutup, tipe: 'Tutup' };
        usageMap[key].needed += qty;
      }

      const spray = p.spray_id ? sprayList?.find(s => s.id === p.spray_id) : null;
      const beratSpray = spray?.berat_gram || 8;
      if (spray) {
        const key = `spray_${spray.id}`;
        usageMap[key] = usageMap[key] || { needed: 0, item: spray, tipe: 'Spray' };
        usageMap[key].needed += qty;
      }

      const cairanWeight = (p.racikan || []).reduce((sum, r) => {
        const vol = ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100) * ukuranMl;
        if (r.satuan === 'liter' || r.kategori?.toLowerCase() === 'alkohol') return sum + (vol * 0.8);
        return sum + vol;
      }, 0);

      let beratKomp = 0;
      (p.komponen_tambahan || []).forEach(k => {
        const matchItem = itemTambahanList.find(it => it.nama === k.nama || it.id === k.id);
        beratKomp += (matchItem?.berat_gram || 5);
        if (matchItem) {
          const key = `item_${matchItem.id}`;
          usageMap[key] = usageMap[key] || { needed: 0, item: matchItem, tipe: 'Item Tambahan' };
          usageMap[key].needed += qty;
        }
      });

      const berat1Pcs = beratBotol + (beratTutup || 0) + (beratSpray || 0) + cairanWeight + beratKomp;
      totalGram += (berat1Pcs * qty);
    });

    const masterBoxWeight = Math.ceil(totalQty / 100) * 100;
    totalGram += masterBoxWeight;
    const totalKg = Math.ceil(totalGram / 1000);

    return {
      orderStats: { totalGram, totalKg, totalQty },
      stockDeficits: deficits,
    };
  }, [products, botolList, tutupList, sprayList, itemTambahanList]);

  const couriers = [
    { name: 'J&T EZ', category: 'Reguler', pricePerKg: 9000, est: '24 - 25 Mei 2026' },
    { name: 'JNE REG', category: 'Reguler', pricePerKg: 10000, est: '23 - 24 Mei 2026' },
    { name: 'JNE JTR', category: 'Kargo', pricePerKg: 4000, minPrice: 40000, est: '25 - 26 Mei 2026' },
    { name: 'Lalamove Same Day', category: 'Sameday', flatPrice: 77500, est: '22 Mei 2026' },
    { name: 'Lalamove Instant', category: 'Instant', flatPrice: 183500, est: '22 Mei 2026' }
  ];

  const shippingCost = 0; // Ongkir dihitung nanti terpisah

  const handleCopyAccount = (num) => {
    navigator.clipboard.writeText(num);
    setCopiedAccount(true);
    toast.success(`Nomor rekening ${num} berhasil disalin!`);
    setTimeout(() => setCopiedAccount(false), 2500);
  };

  const handleSaveAddress = () => {
    if (!newAddress.nama || !newAddress.alamatLengkap) return;
    const baru = { ...newAddress, id: Date.now(), isUtama: false };
    setAddresses([...addresses, baru]);
    setSelectedAddressId(baru.id);
    setShowAddressModal(false);
    setNewAddress({ label: 'Rumah', nama: '', alamatLengkap: '' });
  };

  const handleConfirmOrder = () => {
    setShowPaymentSimulationModal(false);
    onComplete({
      paymentTerms,
      dpPercentage: paymentTerms === 'cash' ? 50 : 0,
      tempoDays: paymentTerms === 'tempo' ? tempoDays : null,
      tanggal_jatuh_tempo: paymentTerms === 'tempo' ? jatuhTempoDate.toISOString() : null,
      rekening_tujuan: activeBank,
      paymentMethod: activeBank.key,
      nominalBayarSekarang,
      sisaPelunasan,
      selectedCourier,
      shippingCost,
      deliveryMethod,
      customerAddress: selectedAddress?.alamatLengkap,
      customerName: selectedAddress?.nama
    });
  };

  return (
    <div className="max-w-5xl mx-auto pb-28">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8 border-b border-[#E6D5B8] pb-6">
        <button 
          onClick={onBack} 
          className="p-2.5 hover:bg-[#F9F5EC] rounded-2xl text-[#8C6208] transition-colors border border-[#E6D5B8] bg-white cursor-pointer"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-black text-[#302621]">Checkout & Termin Pembayaran</h1>
          <p className="text-xs text-[#7A695B] mt-0.5">Tentukan opsi pengiriman, termin pembayaran, dan rekening tujuan transfer.</p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* LEFT COLUMN: Configuration */}
        <div className="flex-1 space-y-6 w-full">
          
          {/* ALAMAT / PENGIRIMAN */}
          <div className="bg-white border border-[#E6D5B8] rounded-3xl p-6 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-black text-[#302621] text-base flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#8C6208]" />
                <span>Metode Pengambilan & Alamat</span>
              </h3>
              <div className="flex bg-[#F9F5EC] p-1 rounded-xl border border-[#E6D5B8]">
                <button
                  type="button"
                  onClick={() => setDeliveryMethod('kurir')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${deliveryMethod === 'kurir' ? 'bg-[#8C6208] text-white shadow-sm' : 'text-slate-600'}`}
                >
                  Kirim Kurir
                </button>
                <button
                  type="button"
                  onClick={() => setDeliveryMethod('pickup')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${deliveryMethod === 'pickup' ? 'bg-[#8C6208] text-white shadow-sm' : 'text-slate-600'}`}
                >
                  Ambil Sendiri
                </button>
              </div>
            </div>

            {deliveryMethod === 'kurir' ? (
              <div className="bg-[#FFFDF9] border border-[#E6D5B8]/80 rounded-2xl p-4 flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-xs text-[#302621]">{selectedAddress?.nama}</span>
                    <span className="text-[10px] bg-[#F9F5EC] text-[#8C6208] border border-[#E6D5B8] px-2 py-0.5 rounded-md font-bold">{selectedAddress?.label}</span>
                  </div>
                  <p className="text-xs text-[#7A695B] mt-1 leading-relaxed">{selectedAddress?.alamatLengkap}</p>
                </div>
                <button 
                  type="button" 
                  onClick={() => setShowAddressModal(true)} 
                  className="text-xs font-bold text-[#8C6208] hover:underline shrink-0 ml-4 cursor-pointer"
                >
                  Ubah Alamat
                </button>
              </div>
            ) : (
              <div className="bg-[#F9F5EC] border border-[#E6D5B8] rounded-2xl p-4 text-xs text-[#7A695B]">
                <p className="font-bold text-[#302621]">Ambil Langsung di Satala Hub / Pabrik Bogor</p>
                <p className="mt-1">Pesanan dapat diambil langsung setelah status produksi dinyatakan selesai & siap ambil.</p>
              </div>
            )}
          </div>

          {/* TERMIN PEMBAYARAN: CASH (DP 50% + 50%) vs TEMPO (KREDIT) */}
          <div className="bg-white border border-[#E6D5B8] rounded-3xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-black text-[#302621] text-base flex items-center gap-2">
                <Wallet className="w-4 h-4 text-[#8C6208]" />
                <span>Termin Pembayaran PO</span>
              </h3>
              <span className="text-[10px] bg-[#F9F5EC] text-[#8C6208] border border-[#E6D5B8] px-2.5 py-1 rounded-full font-bold">
                Pilih Salah Satu
              </span>
            </div>
            <p className="text-xs text-[#7A695B] mb-4">Pilih salah satu skema pembayaran yang disepakati untuk pesanan ini:</p>

            {/* Pilihan Single-Select: Cash vs Tempo */}
            <div className={`grid ${allowedTerms.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} gap-3 mb-4`}>
              {allowedTerms.includes('cash') && (
                <button
                  type="button"
                  onClick={() => setPaymentTerms('cash')}
                  className={`p-4 rounded-2xl border-2 text-left transition-all cursor-pointer relative ${
                    paymentTerms === 'cash' 
                      ? 'border-[#8C6208] bg-[#F9F5EC] shadow-xs ring-1 ring-[#8C6208]/20' 
                      : 'border-slate-200 bg-white hover:border-[#E6D5B8]'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className={`font-black text-sm ${paymentTerms === 'cash' ? 'text-[#8C6208]' : 'text-[#302621]'}`}>
                      💵 Cash (DP 50%)
                    </p>
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      paymentTerms === 'cash' ? 'border-[#8C6208] bg-[#8C6208]' : 'border-slate-300'
                    }`}>
                      {paymentTerms === 'cash' && <Check className="w-2.5 h-2.5 text-white" />}
                    </div>
                  </div>
                  <p className="text-[11px] text-[#7A695B] leading-snug">
                    DP 50% di awal, sisa pelunasan 50% saat barang siap kirim.
                  </p>
                </button>
              )}

              {allowedTerms.includes('tempo') && (
                <button
                  type="button"
                  onClick={() => setPaymentTerms('tempo')}
                  className={`p-4 rounded-2xl border-2 text-left transition-all cursor-pointer relative ${
                    paymentTerms === 'tempo' 
                      ? 'border-[#8C6208] bg-[#F9F5EC] shadow-xs ring-1 ring-[#8C6208]/20' 
                      : 'border-slate-200 bg-white hover:border-[#E6D5B8]'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className={`font-black text-sm ${paymentTerms === 'tempo' ? 'text-[#8C6208]' : 'text-[#302621]'}`}>
                      ⏳ Tempo (Kredit)
                    </p>
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      paymentTerms === 'tempo' ? 'border-[#8C6208] bg-[#8C6208]' : 'border-slate-300'
                    }`}>
                      {paymentTerms === 'tempo' && <Check className="w-2.5 h-2.5 text-white" />}
                    </div>
                  </div>
                  <p className="text-[11px] text-[#7A695B] leading-snug">
                    Pelunasan tagihan nanti sesuai batas tanggal jatuh tempo.
                  </p>
                </button>
              )}
            </div>

            {/* Keterangan Detail Berdasarkan Opsi Terpilih */}
            {paymentTerms === 'cash' && (
              <div className="bg-[#FFFDF9] border border-[#E6D5B8] p-4 rounded-2xl space-y-3">
                <div className="flex items-center justify-between border-b border-[#E6D5B8]/60 pb-2">
                  <span className="font-black text-xs text-[#302621]">Skema Pembayaran Cash Maklon:</span>
                  <span className="text-[10px] bg-[#8C6208]/15 text-[#8C6208] font-black px-2 py-0.5 rounded-md">
                    DP 50% + Pelunasan 50%
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div className="bg-white border border-[#E6D5B8] p-3 rounded-xl">
                    <span className="text-[10px] text-[#7A695B] font-bold block mb-0.5">1. DIBAYAR SEKARANG (DP 50%):</span>
                    <span className="font-black text-base text-[#8C6208]">Rp {fmt(nominalBayarSekarang)}</span>
                    <p className="text-[10px] text-slate-500 mt-1">Uang muka untuk memulai PO item & antrean produksi pabrik.</p>
                  </div>
                  <div className="bg-white border border-[#E6D5B8] p-3 rounded-xl">
                    <span className="text-[10px] text-[#7A695B] font-bold block mb-0.5">2. SAAT SIAP KIRIM (50%):</span>
                    <span className="font-bold text-base text-[#302621]">Rp {fmt(sisaPelunasan)}</span>
                    <p className="text-[10px] text-slate-500 mt-1">Pelunasan dilakukan ketika barang sudah selesai diproduksi & siap kirim.</p>
                  </div>
                </div>

                <div className="bg-[#F9F5EC] border border-[#E6D5B8]/80 rounded-xl p-3 text-[11px] text-[#7A695B] flex items-start gap-2">
                  <Info className="w-4 h-4 text-[#8C6208] shrink-0 mt-0.5" />
                  <p className="leading-relaxed">
                    Setelah transfer DP 50% dikonfirmasi, status pesanan akan langsung diteruskan ke tahapan <strong>Sedang PO Item</strong> atau <strong>Antrean Produksi</strong>.
                  </p>
                </div>
              </div>
            )}

            {paymentTerms === 'tempo' && (
              <div className="bg-[#FFFDF9] border border-[#E6D5B8] p-4 rounded-2xl space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-bold text-[#7A695B] uppercase tracking-wider">Jangka Waktu Tempo:</p>
                  <span className="text-[11px] bg-blue-50 text-blue-800 font-bold px-2 py-0.5 rounded-md border border-blue-200">
                    {tempoDays} Hari ({tempoDays === 30 ? '1 Bulan' : `${tempoDays} Hari`})
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { days: 14, label: '14 Hari' },
                    { days: 30, label: '30 Hari (1 Bulan - Standar)' },
                    { days: 45, label: '45 Hari' },
                    { days: 60, label: '60 Hari (2 Bulan)' }
                  ].map(item => (
                    <button
                      key={item.days}
                      type="button"
                      onClick={() => setTempoDays(item.days)}
                      className={`px-3.5 py-2 rounded-xl text-xs font-black transition-all cursor-pointer ${
                        tempoDays === item.days 
                          ? 'bg-[#8C6208] text-white shadow-sm' 
                          : 'bg-white border border-[#E6D5B8] text-[#302621] hover:bg-[#F9F5EC]'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
                <div className="bg-[#F0F7FF] border border-blue-200 rounded-xl p-3.5 flex items-start gap-2.5 text-xs">
                  <Calendar className="w-4 h-4 text-blue-700 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold text-blue-950 block">Ketentuan Jatuh Tempo:</span>
                    <span className="font-black text-blue-900 text-sm">
                      {tempoDays} Hari ({tempoDays === 30 ? '1 Bulan' : `${tempoDays} Hari`}) Terhitung Setelah Barang Datang
                    </span>
                    <p className="text-[11px] text-blue-800 mt-1 leading-relaxed">
                      Jatuh tempo <strong>tidak dihitung dari hari ini</strong> saat membuat PO, melainkan <strong>mulai berjalan setelah seluruh barang selesai diproduksi & tiba di alamat Kakak</strong> (biasanya 1 bulan setelah PO selesai).
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* REKENING TUJUAN TRANSFER (Back-end Managed) */}
          <div className="bg-white border border-[#E6D5B8] rounded-3xl p-6 shadow-sm">
            <h3 className="font-black text-[#302621] text-base mb-1 flex items-center gap-2">
              <Store className="w-4 h-4 text-[#8C6208]" />
              <span>Rekening Tujuan Transfer Satala</span>
            </h3>
            <p className="text-xs text-[#7A695B] mb-4">Pilih rekening resmi PT Satala Dermatech Essential tujuan transfer:</p>

            <div className="space-y-3">
              {bankOptions.map(bank => {
                const isSelected = selectedBankKey === bank.key;
                return (
                  <div
                    key={bank.key}
                    onClick={() => setSelectedBankKey(bank.key)}
                    className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                      isSelected 
                        ? 'border-[#8C6208] bg-[#F9F5EC]/70 shadow-xs' 
                        : 'border-slate-100 bg-white hover:border-[#E6D5B8]'
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-black text-sm text-[#302621]">{bank.bankName}</span>
                        <span className="text-[10px] bg-[#8C6208]/15 text-[#8C6208] font-bold px-2 py-0.5 rounded-full">
                          {bank.badge}
                        </span>
                      </div>
                      <p className="text-xs text-[#7A695B] font-medium mt-0.5">a.n. {bank.accountName}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="font-mono font-black text-base text-[#8C6208] tracking-wider">
                          {bank.accountNumber}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyAccount(bank.accountNumber);
                          }}
                          className="text-[10px] font-bold text-[#8C6208] hover:underline flex items-center gap-1 bg-white border border-[#E6D5B8] px-2 py-0.5 rounded-md"
                        >
                          <Copy className="w-3 h-3" /> Salin
                        </button>
                      </div>
                    </div>

                    <div className="shrink-0 flex items-center justify-end">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? 'border-[#8C6208] bg-[#8C6208]' : 'border-slate-300'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

        {/* RIGHT COLUMN: Summary Card */}
        <div className="lg:w-[380px] w-full sticky top-24">
          <div className="bg-white border border-[#E6D5B8] rounded-3xl p-6 shadow-sm space-y-4">
            <h3 className="font-black text-[#302621] text-base border-b border-slate-100 pb-3">
              Ringkasan Checkout
            </h3>

            <div className="space-y-2.5 text-xs">
              <div className="flex justify-between text-[#7A695B]">
                <span>Total Produk:</span>
                <span className="font-bold text-[#302621]">{orderStats.totalQty} Pcs</span>
              </div>
              <div className="flex justify-between text-[#7A695B]">
                <span>Brand Pemesan:</span>
                <span className="font-bold text-[#302621]">{activeBrand}</span>
              </div>
              <div className="flex justify-between text-[#7A695B]">
                <span>Termin Pembayaran:</span>
                <span className="font-bold text-[#8C6208] uppercase">
                  {paymentTerms === 'tempo' ? `TEMPO (${tempoDays} HARI)` : 'CASH (DP 50%)'}
                </span>
              </div>
              <div className="flex justify-between text-[#7A695B]">
                <span>Total Nilai Pesanan:</span>
                <span className="font-black text-[#302621] text-sm">Rp {fmt(totalHargaJualSemua)}</span>
              </div>
            </div>

            {/* Total Bayar Sekarang Box */}
            <div className="bg-[#FFFDF9] border border-[#E6D5B8] p-4 rounded-2xl">
              <span className="text-[10px] font-bold text-[#7A695B] uppercase tracking-wider block mb-1">
                {paymentTerms === 'tempo' ? 'Tagihan Jatuh Tempo (Bayar Nanti):' : 'Nominal Transfer DP 50% Sekarang:'}
              </span>
              <p className="text-2xl font-black text-[#8C6208]">
                {paymentTerms === 'tempo' ? `Rp ${fmt(totalHargaJualSemua)}` : `Rp ${fmt(nominalBayarSekarang)}`}
              </p>
              
              {paymentTerms === 'cash' && (
                <div className="text-[11px] text-[#7A695B] mt-2 pt-2 border-t border-[#E6D5B8]/60 space-y-1">
                  <p>• DP 50% di awal: <strong>Rp {fmt(nominalBayarSekarang)}</strong></p>
                  <p>• Pelunasan 50%: <strong>Rp {fmt(sisaPelunasan)}</strong> (saat barang siap kirim)</p>
                </div>
              )}

              {paymentTerms === 'tempo' && (
                <p className="text-[11px] text-blue-900 font-bold mt-1.5 pt-1.5 border-t border-[#E6D5B8]/60">
                  💡 Jatuh tempo {tempoDays} hari ({tempoDays === 30 ? '1 bulan' : `${tempoDays} hari`}) terhitung setelah barang datang / PO selesai.
                </p>
              )}
            </div>

            <Button
              onClick={() => setShowPaymentSimulationModal(true)}
              className="w-full bg-[#302621] hover:bg-[#1A1411] text-white font-black h-12 rounded-xl text-xs flex items-center justify-center gap-2 shadow-sm cursor-pointer transition-all"
            >
              <span>{paymentTerms === 'tempo' ? 'Konfirmasi Pesanan Tempo' : 'Transfer DP 50% Sekarang'}</span>
              <ArrowRight className="w-4 h-4" />
            </Button>

            <p className="text-[10px] text-center text-slate-400 leading-relaxed px-2">
              🔒 Transaksi resmi & tercatat otomatis di sistem PT Satala Dermatech Essential
            </p>
          </div>
        </div>
      </div>

      {/* MODAL 1: POP-UP INSTRUKSI TRANSFER DP 50% ATAU KONFIRMASI TEMPO */}
      {showPaymentSimulationModal && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col border border-[#E6D5B8] animate-in zoom-in-95 duration-200">
            {/* Header Modal */}
            <div className="px-6 py-4 border-b border-[#E6D5B8] bg-[#F9F5EC] flex justify-between items-center">
              <div>
                <span className="text-[10px] font-bold text-[#8C6208] uppercase tracking-wider block">
                  {paymentTerms === 'tempo' ? 'Informasi Termin Tempo' : 'Instruksi Transfer Pembayaran'}
                </span>
                <h3 className="font-black text-base text-[#302621]">{activeBrand} - Repeat Order</h3>
              </div>
              <button 
                onClick={() => setShowPaymentSimulationModal(false)} 
                className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs overflow-y-auto max-h-[80vh]">
              {/* Box Tagihan / Nominal */}
              {paymentTerms === 'cash' ? (
                <div className="text-center bg-[#FFFDF9] border border-[#E6D5B8] rounded-2xl p-4">
                  <span className="text-[#7A695B] font-bold uppercase text-[10px] tracking-wider block mb-1">
                    Jumlah Transfer DP 50% di Awal
                  </span>
                  <p className="text-3xl font-black text-[#8C6208]">
                    Rp {fmt(nominalBayarSekarang)}
                  </p>
                  <p className="text-[11px] text-[#7A695B] mt-1.5 font-medium">
                    Total Pesanan: Rp {fmt(totalHargaJualSemua)}
                  </p>
                </div>
              ) : (
                <div className="text-center bg-[#F0F7FF] border border-blue-200 rounded-2xl p-4">
                  <span className="text-blue-700 font-bold uppercase text-[10px] tracking-wider block mb-1">
                    Total Tagihan Tempo ({tempoDays} Hari / {tempoDays === 30 ? '1 Bulan' : `${tempoDays} Hari`})
                  </span>
                  <p className="text-3xl font-black text-blue-900">
                    Rp {fmt(totalHargaJualSemua)}
                  </p>
                  <div className="inline-flex items-center gap-1.5 bg-white border border-blue-200 px-3 py-1 rounded-full text-blue-800 text-[11px] font-bold mt-2">
                    <Clock className="w-3.5 h-3.5 text-blue-600" />
                    <span>Jatuh Tempo: {tempoDays} Hari Setelah Barang Datang</span>
                  </div>
                </div>
              )}

              {/* Keterangan Aturan Pembayaran Sesuai Termin */}
              {paymentTerms === 'cash' ? (
                <div className="bg-[#F9F5EC] border border-[#E6D5B8] rounded-2xl p-3.5 text-[#302621] space-y-1.5">
                  <div className="flex items-center gap-2 text-[#8C6208] font-black">
                    <Sparkles className="w-4 h-4 shrink-0" />
                    <span>Keterangan Pembayaran Cash Maklon:</span>
                  </div>
                  <p className="text-[11px] text-[#7A695B] leading-relaxed">
                    Mohon lakukan transfer <strong>DP dulu 50%</strong> sebesar <strong className="text-[#8C6208]">Rp {fmt(nominalBayarSekarang)}</strong> untuk memulai proses PO & antrean produksi. Nanti ketika seluruh barang pesanan <strong>sudah selesai dan siap kirim</strong>, dilakukan <strong>pelunasan 50% sisanya (Rp {fmt(sisaPelunasan)})</strong>.
                  </p>
                </div>
              ) : (
                <div className="bg-[#F0F7FF] border border-blue-200 rounded-2xl p-3.5 text-blue-950 space-y-1.5">
                  <div className="flex items-center gap-2 text-blue-800 font-black">
                    <Clock className="w-4 h-4 shrink-0" />
                    <span>Pemberitahuan Termin Tempo dari ASA:</span>
                  </div>
                  <p className="text-[11px] text-blue-900 leading-relaxed">
                    Halo Kak! Pesanan brand <strong>{activeBrand}</strong> akan langsung diproses tanpa uang muka. Tagihan sebesar <strong>Rp {fmt(totalHargaJualSemua)}</strong> akan jatuh tempo <strong>{tempoDays} hari (biasanya 1 bulan) setelah barang pesanan selesai diproduksi & tiba di alamat Kakak</strong>. Pembayaran pelunasan nantinya dapat ditransfer ke rekening resmi PT Satala di bawah ini.
                  </p>
                </div>
              )}

              {/* Box Rekening Tujuan */}
              <div className="bg-white border border-[#E6D5B8] rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <span className="font-bold text-[#302621]">
                    {paymentTerms === 'tempo' ? 'Rekening Tujuan Pelunasan Nanti:' : 'Rekening Tujuan Transfer Satala:'}
                  </span>
                  <span className="text-[10px] bg-[#F9F5EC] text-[#8C6208] font-bold px-2 py-0.5 rounded-full border border-[#E6D5B8]">
                    {activeBank.bankName}
                  </span>
                </div>

                <div className="flex items-center justify-between bg-[#F9F5EC] p-3 rounded-xl border border-[#E6D5B8]/70">
                  <div>
                    <span className="text-[10px] font-bold text-[#7A695B] uppercase block">Nomor Rekening:</span>
                    <span className="font-mono text-lg font-black text-[#302621] tracking-wider">
                      {activeBank.accountNumber}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopyAccount(activeBank.accountNumber)}
                    className="bg-white hover:bg-slate-50 border border-[#E6D5B8] text-[#8C6208] px-3 py-1.5 rounded-xl font-bold text-xs flex items-center gap-1 cursor-pointer shadow-xs"
                  >
                    {copiedAccount ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedAccount ? 'Tersalin' : 'Salin'}</span>
                  </button>
                </div>

                <div>
                  <span className="text-[10px] font-bold text-[#7A695B] uppercase block">Atas Nama Penerima:</span>
                  <span className="font-bold text-sm text-[#302621]">{activeBank.accountName}</span>
                </div>
              </div>

              <div className="bg-[#FFFDF9] border border-[#E6D5B8]/70 rounded-2xl p-3 flex items-start gap-2.5">
                <ShieldCheck className="w-4 h-4 text-[#8C6208] shrink-0 mt-0.5" />
                <p className="text-[11px] text-[#7A695B] leading-relaxed">
                  Setelah menekan konfirmasi di bawah, pesanan Kakak akan diteruskan ke Tim Satala untuk verifikasi dan pemrosesan tahapan produksi.
                </p>
              </div>
            </div>

            {/* Modal Footer CTA */}
            <div className="p-4 bg-[#F9F5EC] border-t border-[#E6D5B8] flex gap-2.5">
              <Button
                variant="outline"
                onClick={() => setShowPaymentSimulationModal(false)}
                className="flex-1 border-[#E6D5B8] bg-white text-[#302621] text-xs font-bold h-11 rounded-xl cursor-pointer"
              >
                Kembali
              </Button>
              <Button
                onClick={handleConfirmOrder}
                className="flex-1 bg-[#302621] hover:bg-[#1A1411] text-white text-xs font-black h-11 rounded-xl flex items-center justify-center gap-1.5 shadow-sm cursor-pointer"
              >
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span>
                  {paymentTerms === 'tempo' ? 'Setujui & Konfirmasi PO Tempo' : 'Saya Sudah Transfer DP 50%'}
                </span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL TAMBAH ALAMAT */}
      {showAddressModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-xl overflow-hidden border border-[#E6D5B8]">
            <div className="px-6 py-4 border-b border-[#E6D5B8] flex justify-between items-center bg-[#F9F5EC]">
              <h3 className="font-bold text-sm text-[#302621]">Tambah Alamat Baru</h3>
              <button onClick={() => setShowAddressModal(false)} className="text-slate-400 hover:text-slate-600">×</button>
            </div>
            <div className="p-5 space-y-3.5 text-xs">
              <div>
                <label className="font-bold text-[#7A695B] mb-1 block">Nama Penerima / Perusahaan</label>
                <Input value={newAddress.nama} onChange={e => setNewAddress({...newAddress, nama: e.target.value})} placeholder="Nama & No. HP Penerima" className="h-10 rounded-xl" />
              </div>
              <div>
                <label className="font-bold text-[#7A695B] mb-1 block">Alamat Lengkap</label>
                <textarea 
                  value={newAddress.alamatLengkap} 
                  onChange={e => setNewAddress({...newAddress, alamatLengkap: e.target.value})} 
                  placeholder="Jalan, Gedung, No. Rumah, Kecamatan, Kota, Kode Pos" 
                  className="w-full border border-slate-200 rounded-xl p-3 text-xs focus:outline-none focus:ring-2 focus:ring-[#8C6208] h-20 resize-none"
                />
              </div>
            </div>
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex gap-2">
              <Button variant="outline" className="flex-1 text-xs rounded-xl" onClick={() => setShowAddressModal(false)}>Batal</Button>
              <Button className="flex-1 bg-[#8C6208] hover:bg-[#6F4E06] text-white text-xs rounded-xl font-bold" onClick={handleSaveAddress}>Simpan</Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
